import moment from "moment";

export const designSystemMixin = {
  methods: {
    // ── Initials ────────────────────────────────────────────────

    /**
     * Returns initials from a full name string.
     * e.g. "John Doe" → "JD"
     */
    dsGetInitials(name) {
      if (!name) return "?";
      return name
        .split(" ")
        .slice(0, 2)
        .map((n) => n[0])
        .join("")
        .toUpperCase();
    },

    /**
     * Returns initials from an email address.
     * Extracts the local part (before @), splits by special chars (_  .  +  -),
     * and takes up to 2 parts.
     * e.g. john_doe@mail.com → "JD", john+alias@mail.com → "JA"
     */
    dsGetEmailInitials(email) {
      if (!email) return "?";
      const local = email.split("@")[0] || email;
      const parts = local.split(/[_.\-+]+/).filter(Boolean);
      return parts
        .slice(0, 2)
        .map((p) => p[0])
        .join("")
        .toUpperCase();
    },

    // ── Status ──────────────────────────────────────────────────

    /**
     * Maps a status string to a ds-pill modifier class.
     */
    dsStatusClass(status) {
      const map = {
        // Generic
        active: "ds-pill--success",
        inactive: "ds-pill--gray",
        pending: "ds-pill--warning",
        approved: "ds-pill--success",
        rejected: "ds-pill--danger",
        cancelled: "ds-pill--danger",
        processing: "ds-pill--info",
        completed: "ds-pill--success",
        waiting_approval: "ds-pill--info",
        waiting_disbursement: "ds-pill--info",
        review: "ds-pill--info",
        in_review: "ds-pill--info",
        draft: "ds-pill--gray",
        // WPS disburse
        disbursed: "ds-pill--success",
        not_yet: "ds-pill--gray",
        failed: "ds-pill--danger",
      };
      return map[String(status).toLowerCase()] || "ds-pill--gray";
    },

    /**
     * Maps a status string to a human-readable label.
     */
    dsStatusLabel(status) {
      const map = {
        active: "Active",
        inactive: "Inactive",
        pending: "Pending",
        approved: "Approved",
        completed: "Completed",
        rejected: "Rejected",
        cancelled: "Cancelled",
        processing: "Processing",
        waiting_approval: "Waiting Approval",
        waiting_disbursement: "Waiting Disbursement",
        failed: "Failed",
        review: "In Review",
        in_review: "In Review",
        draft: "Draft",
        disbursed: "Disbursed",
        not_yet: "Not Yet",
      };
      return map[String(status).toLowerCase()] || status;
    },

    // ── Avatar ──────────────────────────────────────────────────

    /**
     * Avatar color variant based on name hash.
     * Cycles through available colour variants automatically.
     */
    dsAvatarVariant(name) {
      const variants = ["", "--green", "--yellow", "--purple", "--red"];
      if (!name) return "";
      const hash = [...name].reduce((acc, c) => acc + c.charCodeAt(0), 0);
      return variants[hash % variants.length];
    },

    // ── Currency ────────────────────────────────────────────────

    /**
     * Formats a number as Indonesian Rupiah.
     * e.g. 1500000 → "Rp. 1.500.000"
     */
    dsFormatCurrency(value) {
      const num = Number(value) || 0;
      return `Rp. ${num.toLocaleString("id-ID")}`;
    },

    // ── Date ────────────────────────────────────────────────────

    /**
     * Formats a date as "DD MMM YYYY, HH:mm" (with time, UTC+7).
     * Used for transaction timestamps, created_at, etc.
     * e.g. "01 Jan 2025, 14:30"
     * Returns "—" if date is falsy.
     */
    dsFormatDateTime(date) {
      if (!date) return "—";
      return moment(date).utcOffset(7).format("DD MMM YYYY, HH:mm");
    },

    /**
     * Formats a date as "DD MMM YYYY" (date only, no time).
     * Used for due dates, effective dates, created date columns, etc.
     * e.g. "01 Jan 2025"
     * Returns "—" if date is falsy.
     */
    dsFormatDate(date) {
      if (!date) return "—";
      return moment(date).format("DD MMM YYYY");
    },

    /**
     * Formats a date as "MMM YYYY" (month + year only).
     * Used for period/month columns.
     * e.g. "Jan 2025"
     * Returns "—" if date is falsy.
     */
    dsFormatMonth(date) {
      if (!date) return "—";
      return moment(date).format("MMM YYYY");
    },

    // ── Expiry ──────────────────────────────────────────────────

    /**
     * Returns a ds-pill modifier class based on how close an end date is.
     *   expired       → ds-pill--danger
     *   within 30d    → ds-pill--warning
     *   more than 30d → ds-pill--success
     *   no date       → ds-pill--gray
     */
    dsExpiryClass(endDate) {
      if (!endDate) return "ds-pill--gray";
      const daysLeft = moment(endDate, "YYYY-MM-DD").diff(moment(), "days");
      if (daysLeft < 0) return "ds-pill--danger";
      if (daysLeft <= 30) return "ds-pill--warning";
      return "ds-pill--success";
    },

    /**
     * Returns a human-readable expiry label with smart time unit:
     *   past:  "Expired Xd ago" / "Expired Xmo ago" / "Expired Xyr ago"
     *   today: "Expires today"
     *   ≤30d:  "Xd left"
     *   <1yr:  "Xmo left"
     *   ≥1yr:  "Xyr left" or "Xyr Xmo left"
     */
    dsExpiryLabel(endDate) {
      if (!endDate) return "—";
      const daysLeft = moment(endDate, "YYYY-MM-DD").diff(moment(), "days");

      if (daysLeft === 0) return "Expires today";

      if (daysLeft < 0) {
        const abs = Math.abs(daysLeft);
        if (abs >= 365) return `Expired ${Math.floor(abs / 365)}yr ago`;
        if (abs >= 30) return `Expired ${Math.floor(abs / 30)}mo ago`;
        return `Expired ${abs}d ago`;
      }

      if (daysLeft <= 30) return `${daysLeft}d left`;
      if (daysLeft < 365) return `${Math.floor(daysLeft / 30)}mo left`;
      const yrs = Math.floor(daysLeft / 365);
      const mos = Math.floor((daysLeft % 365) / 30);
      return mos > 0 ? `${yrs}yr ${mos}mo left` : `${yrs}yr left`;
    },
  },
};
